/**
 * The expanded Holdings page's four pictures, as numbers — pure, so each is a test.
 * The design is `docs/mockups/holdings-13f.html` §3b.
 */
import type { HoldingStatus, HoldingsSymbol } from "../api/holdings";

export interface FundMeta {
  id: string;
  name: string;
  manager: string;
  order: number | null;
}

export function fundMeta(h: HoldingsSymbol, id: string): FundMeta {
  const f = h.funds[id];
  return { id, name: f?.name ?? id, manager: f?.manager ?? id, order: f?.order ?? null };
}

// ── exposure: who is long, who is hedged ───────────────────────────────────
export interface ExposureRow {
  fund: FundMeta;
  shares: number;
  calls: number;
  puts: number;
  status: HoldingStatus;
  /** Each row is 100% of that fund's gross, so a hedge reads the same at $5M as at $5B. */
  sharesW: number;
  callsW: number;
  putsW: number;
}

export function exposure(h: HoldingsSymbol): ExposureRow[] {
  const rows = [...h.holders, ...h.optionsOnly.filter((o) => !h.holders.some((x) => x.fund === o.fund))];
  return rows
    .map((x) => {
      const gross = Math.max(1, x.value + x.calls + x.puts);
      return { fund: fundMeta(h, x.fund), shares: x.value, calls: x.calls, puts: x.puts, status: x.status,
               sharesW: x.value / gross, callsW: x.calls / gross, putsW: x.puts / gross };
    })
    .sort((a, b) => b.shares + b.calls - (a.shares + a.calls));
}

// ── conviction: $ held against the share of the fund's own book ────────────
export interface ConvictionPoint {
  fund: FundMeta;
  value: number;
  pctBook: number;
  /** |trade| as a share of the earlier position, capped — the dot's size. */
  change: number;
  status: HoldingStatus;
}

export function conviction(h: HoldingsSymbol): ConvictionPoint[] {
  return h.holders
    .filter((x) => x.value > 0 && x.pctBook !== null)
    .map((x) => {
      const before = x.value - (x.trade ?? 0);
      const change = x.status === "NEW" ? 1 : before > 0 && x.trade !== null ? Math.min(1, Math.abs(x.trade) / before) : 0;
      return { fund: fundMeta(h, x.fund), value: x.value, pctBook: x.pctBook ?? 0, change, status: x.status };
    });
}

/** Log-scale x positions for $ held, in decades that bracket every point. */
export function logScale(values: number[]): { lo: number; hi: number; x: (v: number) => number; ticks: number[] } {
  const logs = values.filter((v) => v > 0).map(Math.log10);
  const lo = logs.length ? Math.floor(Math.min(...logs)) : 6;
  const hi = logs.length ? Math.max(lo + 1, Math.ceil(Math.max(...logs))) : 10;
  const ticks = Array.from({ length: hi - lo + 1 }, (_, i) => 10 ** (lo + i));
  return { lo, hi, x: (v) => (Math.log10(Math.max(v, 10 ** lo)) - lo) / (hi - lo), ticks };
}

// ── waterfall: the earlier quarter → price → trades → the later quarter ────
export interface WaterStep {
  key: string;
  label: string;
  value: number;
  kind: "total" | "price" | "trade" | "others";
  fund?: FundMeta;
  status?: HoldingStatus;
  lo: number;
  hi: number;
}

export function heldAt(h: HoldingsSymbol, period: string): number {
  const i = h.quarters.indexOf(period);
  if (i < 0) return 0;
  return Object.values(h.history).reduce((s, row) => s + (row[i] ?? 0), 0);
}

/**
 * The base quarter as a bridge from the one before it. **The price move is what
 * is left when the trades are taken out** — `trade` already excludes the price,
 * so `held(after) = held(before) + price + Σ trades` closes exactly, with no
 * rounding bar and no second estimate of the price.
 */
export function waterfall(h: HoldingsSymbol, minShare = 0.01): WaterStep[] {
  const after = h.window.base;
  const i = h.quarters.indexOf(after);
  const before = i > 0 ? h.quarters[i - 1] : undefined;
  const q = h.ledger.find((x) => x.period === after);
  if (!before || !q) return [];
  const h0 = heldAt(h, before), h1 = heldAt(h, after);
  const moves = [...q.adds, ...q.cuts].sort((a, b) => b.trade - a.trade);
  const sumTrades = moves.reduce((s, m) => s + m.trade, 0);
  const biggest = Math.max(1, ...moves.map((m) => Math.abs(m.trade)));
  const named = moves.filter((m) => Math.abs(m.trade) >= minShare * biggest);
  const others = moves.filter((m) => !named.includes(m));
  const raw: Omit<WaterStep, "lo" | "hi">[] = [
    { key: "start", label: `${label(before)} held`, value: h0, kind: "total" },
    { key: "price", label: "price move", value: h1 - h0 - sumTrades, kind: "price" },
    ...named.map((m) => ({ key: m.fund, label: fundMeta(h, m.fund).name, value: m.trade, kind: "trade" as const,
                           fund: fundMeta(h, m.fund), status: m.status })),
    ...(others.length ? [{ key: "others", label: `${others.length} others`, value: others.reduce((s, m) => s + m.trade, 0), kind: "others" as const }] : []),
    { key: "end", label: `${label(after)} held`, value: h1, kind: "total" },
  ];
  let run = 0;
  return raw.map((s) => {
    if (s.kind === "total") {
      run = s.value;
      return { ...s, lo: 0, hi: s.value };
    }
    const lo = Math.min(run, run + s.value), hi = Math.max(run, run + s.value);
    run += s.value;
    return { ...s, lo, hi };
  });
}

// ── ownership over time ────────────────────────────────────────────────────
export interface HeatCell {
  value: number | null;
  /** 0..1 of this fund's own peak in the year. */
  shade: number;
  mark: "entered" | "exited" | null;
  pending: boolean;
}

export interface HeatRow {
  fund: FundMeta;
  cells: HeatCell[];
}

export function ownership(h: HoldingsSymbol): HeatRow[] {
  const n = h.quarters.length;
  const pendingCol = h.window.filedFunds1Q < h.window.funds ? h.quarters.indexOf(h.window.qn) : -1;
  return Object.entries(h.history)
    .map(([fund, row]) => {
      const peak = Math.max(1, ...row.map((v) => v ?? 0));
      const filedNewest = h.holders.some((x) => x.fund === fund && x.period === h.window.qn);
      const cells: HeatCell[] = row.slice(0, n).map((v, i) => {
        const prev = i > 0 ? row[i - 1] ?? 0 : null;
        const cur = v ?? 0;
        const pending = i === pendingCol && !filedNewest;
        return {
          value: v, shade: cur / peak, pending,
          mark: pending || prev === null ? null : prev === 0 && cur > 0 ? "entered" : prev > 0 && cur === 0 ? "exited" : null,
        };
      });
      return { fund: fundMeta(h, fund), cells };
    })
    .sort((a, b) => latest(b) - latest(a));
}

function latest(r: HeatRow): number {
  for (let i = r.cells.length - 1; i >= 0; i--) {
    const c = r.cells[i];
    if (c && !c.pending && c.value) return c.value;
  }
  return 0;
}

function label(period: string): string {
  const [y = 0, m = 1] = period.split("-").map(Number);
  return `Q${Math.ceil(m / 3)} ${String(y % 100).padStart(2, "0")}`;
}
